import posthog from 'posthog-js'
import { initMetaPixel } from '@/lib/metaPixel'
import { formatPLN } from '@/utils/currency'
import { enableAnalyticsPersistence } from '@/composables/useAnalytics'

// ─── Analytics (PostHog, cookieless) ───
// Marketing pages run PostHog with in-memory persistence: no cookies and no
// identifiers survive the session, so no consent banner is required (RODO).
// The logged-in app (useAnalytics.ts) switches to full persistence after the
// user accepts analytics cookies.
function initMarketingAnalytics() {
  const apiKey = document.querySelector('meta[name="posthog-api-key"]')?.content
  if (!apiKey) return null

  const apiHost =
    document.querySelector('meta[name="posthog-host"]')?.content ||
    'https://eu.i.posthog.com'
  posthog.init(apiKey, {
    api_host: apiHost,
    persistence: 'memory',
    capture_pageview: true,
    capture_pageleave: true,
    loaded: (ph) => {
      if (import.meta.env?.DEV) ph.opt_out_capturing()
    },
  })
  return posthog
}

function initScrollReveals() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  const fadeEls = document.querySelectorAll('.fade-up, [data-reveal-prose] > *')

  if (
    reduceMotion.matches ||
    !('IntersectionObserver' in window) ||
    !fadeEls.length
  ) {
    fadeEls.forEach((element) => element.classList.add('visible'))
    return reduceMotion
  }

  document.querySelectorAll('[data-reveal-group]').forEach((group) => {
    group.querySelectorAll(':scope > .fade-up').forEach((element, index) => {
      element.style.setProperty(
        '--reveal-delay',
        `${Math.min(index, 3) * 50}ms`,
      )
    })
  })

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
          observer.unobserve(entry.target)
        }
      })
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -4% 0px',
    },
  )

  fadeEls.forEach((element) => observer.observe(element))
  return reduceMotion
}

const reduceMotion = initScrollReveals()

document.addEventListener('DOMContentLoaded', () => {
  initMarketingAnalytics()
  initMetaPixel() // self-gates on cookie consent; no-op until "Akceptuję"

  document.querySelectorAll('[data-pln-value]').forEach((element) => {
    const value = Number(element.dataset.plnValue)
    if (!Number.isNaN(value)) element.textContent = formatPLN(value)
  })

  // ─── Cookie consent (marketing pages) ───
  // Shares the `cookie_consent` key with the in-app CookieConsent.vue. The
  // Meta Pixel only starts after "Akceptuję". PostHog here is cookieless, so
  // it runs regardless.
  const banner = document.getElementById('cookie-banner')
  if (banner && !localStorage.getItem('cookie_consent')) {
    banner.classList.remove('hidden')
  }
  document
    .getElementById('cookie-accept-all')
    ?.addEventListener('click', () => {
      localStorage.setItem('cookie_consent', 'all')
      banner?.classList.add('hidden')
      enableAnalyticsPersistence()
      initMetaPixel()
    })
  document
    .getElementById('cookie-accept-necessary')
    ?.addEventListener('click', () => {
      localStorage.setItem('cookie_consent', 'necessary')
      banner?.classList.add('hidden')
    })

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-analytics-event]')
    if (!target) return

    posthog.capture(target.dataset.analyticsEvent, {
      cta_location: target.dataset.analyticsLocation,
      page_path: window.location.pathname,
    })
  })

  // ─── Mobile menu ───
  const mobileMenu = document.getElementById('mobile-menu')
  const backdrop = document.getElementById('mobile-menu-backdrop')

  function openMenu() {
    mobileMenu?.classList.remove('hidden')
  }
  function closeMenu() {
    mobileMenu?.classList.add('hidden')
  }

  document
    .getElementById('mobile-menu-open')
    ?.addEventListener('click', openMenu)
  document
    .getElementById('mobile-menu-close')
    ?.addEventListener('click', closeMenu)
  backdrop?.addEventListener('click', closeMenu)

  // Close the drawer on any menu link click — same-page anchors (/#funkcje)
  // navigate without a reload, so the menu would otherwise stay open.
  mobileMenu
    ?.querySelectorAll('a')
    .forEach((a) => a.addEventListener('click', closeMenu))

  // ─── Pricing toggle (monthly ↔ annual) ───
  const toggle = document.getElementById('pricing-toggle')
  const monthlyLabel = document.getElementById('label-monthly')
  const annualLabel = document.getElementById('label-annual')

  if (toggle) {
    toggle.addEventListener('click', () => {
      const isAnnual = toggle.getAttribute('aria-checked') === 'true'
      const next = !isAnnual
      toggle.setAttribute('aria-checked', String(next))

      const knob = toggle.querySelector('span[aria-hidden]')
      knob.style.transform = next ? 'translateX(20px)' : 'translateX(0)'
      toggle.style.backgroundColor = next ? '#18181b' : '#d4d4d8'

      if (monthlyLabel) {
        monthlyLabel.style.fontWeight = next ? '400' : '600'
        monthlyLabel.style.color = next ? '#71717a' : '#18181b'
      }
      if (annualLabel) {
        annualLabel.style.fontWeight = next ? '600' : '400'
        annualLabel.style.color = next ? '#18181b' : '#71717a'
      }

      document
        .querySelectorAll('[data-monthly-price]')
        .forEach((el) => el.classList.toggle('hidden', next))
      document
        .querySelectorAll('[data-annual-price]')
        .forEach((el) => el.classList.toggle('hidden', !next))
      document
        .querySelectorAll('[data-monthly-period]')
        .forEach((el) => el.classList.toggle('hidden', next))
      document
        .querySelectorAll('[data-annual-period]')
        .forEach((el) => el.classList.toggle('hidden', !next))
    })
  }

  // ─── FAQ accordion ───
  document.querySelectorAll('.faq-toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.faq-item')
      const answer = item.querySelector('.faq-answer')
      const icon = item.querySelector('.faq-icon')
      const isOpen = btn.getAttribute('aria-expanded') === 'true'

      // Close all others
      document.querySelectorAll('.faq-item').forEach((other) => {
        if (other !== item) {
          other.querySelector('.faq-answer')?.classList.add('hidden')
          other
            .querySelector('.faq-toggle')
            ?.setAttribute('aria-expanded', 'false')
          other.querySelector('.faq-icon')?.classList.remove('rotate-45')
        }
      })

      // Toggle current
      btn.setAttribute('aria-expanded', String(!isOpen))
      answer.classList.toggle('hidden', isOpen)
      icon.classList.toggle('rotate-45', !isOpen)
    })
  })

  // ─── Smooth scroll for anchor links ───
  // Accept both same-page and root-page anchors.
  document.querySelectorAll('a[href^="#"], a[href^="/#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href')
      const hash = href.replace(/^\//, '')
      if (hash === '#' || !hash.startsWith('#')) return
      const target = document.querySelector(hash)
      if (target) {
        // Target is on this page, so smooth-scroll instead of reloading.
        e.preventDefault()
        target.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        })
        closeMenu()
      }
    })
  })
})
